import React, { useState, useEffect, memo } from 'react';
import axios from 'axios';
import AdminSidebarRaw from '../Components/AdminSidebar';
import { API_BASE_URL } from '../config';
import '../Styles/Categories.css';

const AdminSidebar = memo(AdminSidebarRaw);

const Categories = () => {
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [subSubCategories, setSubSubCategories] = useState([]);

    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);

    // Search States
    const [searchCat, setSearchCat] = useState('');
    const [searchSubCat, setSearchSubCat] = useState('');
    const [searchSubSubCat, setSearchSubSubCat] = useState('');
    const [modalFilterId, setModalFilterId] = useState(null);
    const [modalFilterSubId, setModalFilterSubId] = useState(null);

    // Quick Add States
    const [newCatName, setNewCatName] = useState('');
    const [newCatFile, setNewCatFile] = useState(null);
    const [newCatPreview, setNewCatPreview] = useState(null);

    const [newSubCatName, setNewSubCatName] = useState('');
    const [newSubCatFile, setNewSubCatFile] = useState(null);
    const [newSubCatPreview, setNewSubCatPreview] = useState(null);

    const [newSubSubCatName, setNewSubSubCatName] = useState('');
    const [newSubSubCatFile, setNewSubSubCatFile] = useState(null);
    const [newSubSubCatPreview, setNewSubSubCatPreview] = useState(null);

    // Editing States
    const [editingItem, setEditingItem] = useState({ type: null, id: null });
    const [editValue, setEditValue] = useState('');
    const [editFile, setEditFile] = useState(null);
    const [editPreview, setEditPreview] = useState(null);
    // Dialog States
    const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: null, id: null });
    const [listModal, setListModal] = useState({ show: false, type: null, title: '' });
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 640);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchCategories();
        fetchSubCategories();
        fetchSubSubCategories();
    }, []);

    const fetchCategories = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            const response = await axios.get(`${API_BASE_URL}/admin/categories`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setCategories(response.data);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchSubCategories = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            const response = await axios.get(`${API_BASE_URL}/admin/subcategories`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setSubCategories(response.data);
        } catch (error) {
            console.error('Error fetching subcategories:', error);
        }
    };

    const fetchSubSubCategories = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            const response = await axios.get(`${API_BASE_URL}/admin/subsubcategories`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setSubSubCategories(response.data);
        } catch (error) {
            console.error('Error fetching subsubcategories:', error);
        }
    };

    const handleAdd = async (type, name, parentId = null, file = null) => {
        if (!name.trim()) return;
        try {
            const formData = new FormData();
            formData.append('name', name);
            if (file) formData.append('photo', file);
            if (type === 'subcategories') formData.append('category_id', parentId);
            if (type === 'subsubcategories') formData.append('subcategory_id', parentId);
            const adminToken = localStorage.getItem('adminToken');
            await axios.post(`${API_BASE_URL}/admin/${type}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${adminToken}`
                }
            });

            // Refresh & Reset
            if (type === 'categories') {
                fetchCategories(); setNewCatName(''); setNewCatFile(null); setNewCatPreview(null);
            }
            if (type === 'subcategories') {
                fetchSubCategories(); setNewSubCatName(''); setNewSubCatFile(null); setNewSubCatPreview(null);
            }
            if (type === 'subsubcategories') {
                fetchSubSubCategories(); setNewSubSubCatName(''); setNewSubSubCatFile(null); setNewSubSubCatPreview(null);
            }
        } catch (error) {
            console.error('Error adding:', error);
            alert(error.response?.data?.message || 'Failed to add item');
        }
    };

    const handleUpdate = async (type, id, newName, newFile) => {
        if (editingItem.id !== id || editingItem.type !== type) return;
        if (!newName.trim()) return setEditingItem({ type: null, id: null });

        try {
            const formData = new FormData();
            formData.append('name', newName);
            if (newFile) formData.append('photo', newFile);

            const adminToken = localStorage.getItem('adminToken');
            await axios.put(`${API_BASE_URL}/admin/${type}/${id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${adminToken}`
                }
            });

            setEditingItem({ type: null, id: null });
            setEditFile(null);
            setEditPreview(null);

            if (type === 'categories') fetchCategories();
            if (type === 'subcategories') fetchSubCategories();
            if (type === 'subsubcategories') fetchSubSubCategories();
        } catch (error) {
            console.error('Error updating:', error);
            alert('Failed to update: ' + (error.response?.data?.message || error.message));
        }
    };

    const handleDelete = async () => {
        const { type, id } = deleteConfirm;
        const adminToken = localStorage.getItem('adminToken');
        try {
            await axios.delete(`${API_BASE_URL}/admin/${type}/${id}`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            fetchCategories();
            fetchSubCategories();
            fetchSubSubCategories();
            if (type === 'categories' && selectedCategory?.id === id) setSelectedCategory(null);
            if (type === 'subcategories' && selectedSubCategory?.id === id) setSelectedSubCategory(null);
            setDeleteConfirm({ show: false, type: null, id: null });
        } catch (error) {
            console.error('Error deleting:', error);
            setDeleteConfirm({ show: false, type: null, id: null });
        }
    };

    const openListModal = (type, title) => {
        setModalFilterId(null);
        setModalFilterSubId(null);
        setListModal({ show: true, type, title });
    };

    const closeListModal = () => {
        setListModal({ show: false, type: null, title: '' });
    };

    const confirmDelete = (type, id) => {
        setDeleteConfirm({ show: true, type, id });
    };

    const startEditing = (type, item) => {
        setEditingItem({ type, id: item.id });
        setEditValue(item.name);
        setEditPreview(item.photo ? `${API_BASE_URL}${item.photo}` : null);
        setEditFile(null);
    };

    const handleFileChange = (e, setFile, setPreview) => {
        const file = e.target.files[0];
        if (file) {
            setFile(file);
            setPreview(URL.createObjectURL(file));
        }
    };


    return (
        <div className="categories-container">
            <AdminSidebar />
            <div className="categories-main">
                <div className="hierarchy-grid">
                    {/* Section 1: Categories */}
                    <div className="hierarchy-section">
                        <div className="section-header">
                            <h3>Categories</h3>
                            <button className="view-all-btn" onClick={() => openListModal('categories', 'All Categories')}>View All</button>
                        </div>
                        <div className="quick-add-column">
                            <div className="form-group">
                                <input
                                    type="text"
                                    placeholder="Category Name"
                                    value={newCatName}
                                    onChange={(e) => setNewCatName(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleAdd('categories', newCatName, null, newCatFile)}
                                />
                            </div>
                            <div className="form-group">
                                <div className="photo-upload-container">
                                    <label className="file-upload-label">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleFileChange(e, setNewCatFile, setNewCatPreview)}
                                        />
                                        <span>{newCatFile ? 'Change Image' : 'Browse Image'}</span>
                                    </label>
                                    {newCatPreview && (
                                        <div className="preview-container">
                                            <img src={newCatPreview} alt="Preview" className="preview-img" />
                                            <button className="clear-preview" onClick={() => { setNewCatFile(null); setNewCatPreview(null); }}>×</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <button className="submit-add-btn" onClick={() => handleAdd('categories', newCatName, null, newCatFile)}>
                                Add Category
                            </button>
                        </div>
                    </div>

                    {/* Section 2: Sub-Categories */}
                    <div className="hierarchy-section">
                        <div className="section-header">
                            <h3>Sub Categories</h3>
                            <button className="view-all-btn" onClick={() => openListModal('subcategories', 'All Sub-Categories')}>View All</button>
                        </div>
                        <div className="quick-add-column">
                            <div className="form-group">
                                <select
                                    className="parent-select"
                                    value={selectedCategory?.id || ''}
                                    onChange={(e) => setSelectedCategory(categories.find(c => c.id === parseInt(e.target.value)))}
                                >
                                    <option value="">-- Select Category --</option>
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <input
                                    type="text"
                                    placeholder="Sub-Category Name"
                                    value={newSubCatName}
                                    onChange={(e) => setNewSubCatName(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleAdd('subcategories', newSubCatName, selectedCategory?.id, newSubCatFile)}
                                />
                            </div>
                            <div className="form-group">
                                <div className="photo-upload-container">
                                    <label className="file-upload-label">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleFileChange(e, setNewSubCatFile, setNewSubCatPreview)}
                                        />
                                        <span>{newSubCatFile ? 'Change Image' : 'Browse Image'}</span>
                                    </label>
                                    {newSubCatPreview && (
                                        <div className="preview-container">
                                            <img src={newSubCatPreview} alt="Preview" className="preview-img" />
                                            <button className="clear-preview" onClick={() => { setNewSubCatFile(null); setNewSubCatPreview(null); }}>×</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <button className="submit-add-btn" onClick={() => handleAdd('subcategories', newSubCatName, selectedCategory?.id, newSubCatFile)}>
                                Add Sub-Category
                            </button>
                        </div>
                    </div>

                    {/* Section 3: Sub-Sub-Categories */}
                    <div className="hierarchy-section">
                        <div className="section-header">
                            <h3>Specialized</h3>
                            <button className="view-all-btn" onClick={() => openListModal('subsubcategories', 'All Specialized')}>View All</button>
                        </div>
                        <div className="quick-add-column">
                            <div className="form-group">
                                <select
                                    className="parent-select"
                                    value={selectedSubCategory?.id || ''}
                                    onChange={(e) => setSelectedSubCategory(subCategories.find(sc => sc.id === parseInt(e.target.value)))}
                                >
                                    <option value="">-- Select Sub-Category --</option>
                                    {subCategories.map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <input
                                    type="text"
                                    placeholder="Specialized Name"
                                    value={newSubSubCatName}
                                    onChange={(e) => setNewSubSubCatName(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleAdd('subsubcategories', newSubSubCatName, selectedSubCategory?.id, newSubSubCatFile)}
                                />
                            </div>
                            <div className="form-group">
                                <div className="photo-upload-container">
                                    <label className="file-upload-label">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleFileChange(e, setNewSubSubCatFile, setNewSubSubCatPreview)}
                                        />
                                        <span>{newSubSubCatFile ? 'Change Image' : 'Browse Image'}</span>
                                    </label>
                                    {newSubSubCatPreview && (
                                        <div className="preview-container">
                                            <img src={newSubSubCatPreview} alt="Preview" className="preview-img" />
                                            <button className="clear-preview" onClick={() => { setNewSubSubCatFile(null); setNewSubSubCatPreview(null); }}>×</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <button className="submit-add-btn" onClick={() => handleAdd('subsubcategories', newSubSubCatName, selectedSubCategory?.id, newSubSubCatFile)}>
                                Add Specialized
                            </button>
                        </div>
                    </div>

                </div>
            </div>

            {/* List Management Modal */}
            {listModal.show && (
                <div className="modal-overlay">
                    <div className="list-management-dialog">
                        <div className="modal-header">
                            <div className="modal-header-left">
                                <h3>{listModal.title}</h3>
                                <div className="modal-search-inline">
                                    <input
                                        type="text"
                                        placeholder={`Search ${listModal.title}...`}
                                        value={
                                            listModal.type === 'categories' ? searchCat :
                                                listModal.type === 'subcategories' ? searchSubCat :
                                                    searchSubSubCat
                                        }
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (listModal.type === 'categories') setSearchCat(val);
                                            if (listModal.type === 'subcategories') setSearchSubCat(val);
                                            if (listModal.type === 'subsubcategories') setSearchSubSubCat(val);
                                        }}
                                    />
                                </div>
                            </div>
                            <button className="close-modal-btn" onClick={closeListModal}>×</button>
                        </div>

                        {/* Modal Filtration Buttons */}
                        {listModal.type === 'subcategories' && (
                            <div className="modal-filters">
                                <button
                                    className={`filter-btn ${modalFilterId === null ? 'active' : ''}`}
                                    onClick={() => setModalFilterId(null)}
                                >
                                    All Categories
                                </button>
                                {categories.map(cat => (
                                    <button
                                        key={cat.id}
                                        className={`filter-btn ${modalFilterId === cat.id ? 'active' : ''}`}
                                        onClick={() => setModalFilterId(cat.id)}
                                    >
                                        {cat.name}
                                    </button>
                                ))}
                            </div>
                        )}

                        {listModal.type === 'subsubcategories' && (
                            <div className="modal-filters">
                                <button
                                    className={`filter-btn ${modalFilterSubId === null ? 'active' : ''}`}
                                    onClick={() => setModalFilterSubId(null)}
                                >
                                    All Sub Categories
                                </button>
                                {subCategories.map(sc => (
                                    <button
                                        key={sc.id}
                                        className={`filter-btn ${modalFilterSubId === sc.id ? 'active' : ''}`}
                                        onClick={() => setModalFilterSubId(sc.id)}
                                    >
                                        {sc.name}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="modal-items-list">
                            {(listModal.type === 'categories' ? categories.filter(c => c.name.toLowerCase().includes(searchCat.toLowerCase())) :
                                listModal.type === 'subcategories' ? subCategories
                                    .filter(sc => modalFilterId === null || sc.category_id === modalFilterId)
                                    .filter(sc => sc.name.toLowerCase().includes(searchSubCat.toLowerCase())) :
                                    subSubCategories
                                        .filter(ssc => modalFilterSubId === null || ssc.subcategory_id === modalFilterSubId)
                                        .filter(ssc => ssc.name.toLowerCase().includes(searchSubSubCat.toLowerCase()))
                            ).map(item => (
                                <div key={item.id} className="hierarchy-item modal-item">
                                    <div className="item-info">
                                        {editingItem.type === listModal.type && editingItem.id === item.id && !isMobile ? (
                                            <div className="edit-box">
                                                <div className="edit-upload-wrapper">
                                                    {editPreview && <img src={editPreview} alt="" className="edit-preview-img" />}
                                                    <label className="file-upload-label-small">
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(e) => handleFileChange(e, setEditFile, setEditPreview)}
                                                        />
                                                        <span>{editPreview ? 'Change' : 'Image'}</span>
                                                    </label>
                                                </div>
                                                <input
                                                    className="inline-edit-input"
                                                    autoFocus
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleUpdate(listModal.type, item.id, editValue, editFile)}
                                                />
                                                <div className="edit-btns">
                                                    <button className="save-btn-small" onMouseDown={() => handleUpdate(listModal.type, item.id, editValue, editFile)}>Save</button>
                                                    <button className="cancel-btn-small" onMouseDown={() => setEditingItem({ type: null, id: null })}>Cancel</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="item-content-wrapper">
                                                {item.photo && <img src={`${API_BASE_URL}${item.photo}`} alt="" className="item-thumbnail" />}
                                                <div className="item-text">
                                                    <span className="item-name">{item.name}</span>
                                                    {item.categoryName && <span className="item-parent">In {item.categoryName}</span>}
                                                    {item.subCategoryName && <span className="item-parent">In {item.subCategoryName}</span>}
                                                    {item.productCount !== undefined && <span className="item-count">{item.productCount} Products</span>}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="item-actions">
                                        <button onClick={() => startEditing(listModal.type, item)}>Edit</button>
                                        <button className="del" onClick={() => confirmDelete(listModal.type, item.id)}>Delete</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Delete Confirmation Dialog */}
            {deleteConfirm.show && (
                <div className="modal-overlay delete-overlay">
                    <div className="confirm-dialog">
                        <h3>Confirm Delete</h3>
                        <p>Are you sure you want to delete this item? This action cannot be undone.</p>
                        <div className="dialog-btns">
                            <button className="dialog-cancel-btn" onClick={() => setDeleteConfirm({ show: false, type: null, id: null })}>No, Cancel</button>
                            <button className="dialog-delete-btn" onClick={handleDelete}>Yes, Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile Edit Modal */}
            {editingItem.id && isMobile && (
                <div className="modal-overlay">
                    <div className="confirm-dialog edit-dialog-mobile">
                        <h3>Edit Item</h3>
                        <div className="edit-box-mobile">
                            <div className="edit-upload-wrapper-mobile">
                                <label className="file-upload-label">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFileChange(e, setEditFile, setEditPreview)}
                                    />
                                    <span>{editPreview ? 'Change Image' : 'Browse Image'}</span>
                                </label>
                                {editPreview && (
                                    <div className="preview-container">
                                        <img src={editPreview} alt="Preview" className="preview-img" />
                                        <button className="clear-preview" onClick={() => { setEditFile(null); setEditPreview(null); }}>×</button>
                                    </div>
                                )}
                            </div>
                            <input
                                className="form-group input mobile-edit-input"
                                style={{ width: '100%', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', marginTop: '15px', marginBottom: '20px' }}
                                autoFocus
                                placeholder="Name"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleUpdate(editingItem.type, editingItem.id, editValue, editFile)}
                            />
                        </div>
                        <div className="dialog-btns">
                            <button className="dialog-cancel-btn" onClick={() => setEditingItem({ type: null, id: null })}>Cancel</button>
                            <button className="submit-add-btn" style={{ width: 'auto', padding: '10px 24px', margin: 0 }} onClick={() => handleUpdate(editingItem.type, editingItem.id, editValue, editFile)}>Save Changes</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Categories;
